// Mirrors the SDK session onto an app's root state so components can read
// `root.signedIn`, `root.currentUserId`, and
// `root.jwtClaims.workspace_org_id` without depending on gov-specific
// modules. All three editor apps (canvas, CMS, gov, future consumers)
// point at the same SDK auth pipeline, so subscribing here gives every
// origin the same identity view.
//
// Usage:
//   onCreate: (el, s) => el.call('mirrorAuthSession', s.root || s)
// Pair with `stopMirrorAuthSession` from the returned unsubscriber if
// you need a tear-down; otherwise the subscription lives for the
// lifetime of the page.

import { getSDK } from './sdk.js'

// Detect whether ANY Supabase project has a persisted session.
// Per-project tokens are namespaced by project ref
// (sb-<ref>-auth-token), so a user signed into multiple projects has
// one key per project — any presence counts as "signed in locally".
export const hasSessionToken = function hasSessionToken() {
  if (typeof window === 'undefined') return false
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i)
    if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) return true
  }
  return false
}

// Subscribe to SDK auth events and mirror the session onto root state.
// Idempotent — repeat calls reuse the existing subscription via a symbol
// on `root`.
export const mirrorAuthSession = function mirrorAuthSession(root, onSignIn) {
  if (!root || root._authMirrorInstalled) return root?._authMirrorInstalled
  root._authMirrorInstalled = true

  // migrated 2026-04-27 to SDK onAuthStateChange (signature is event, session;
  // matches Supabase's shape so the callback body is unchanged).
  let lastUserId = null
  const unsubscribe = getSDK().onAuthStateChange((_event, session) => {
    const u = session?.user
    const nextEmail = u?.email || ''
    const nextId = u?.id || ''
    const nextSignedIn = !!u
    const nextClaims = u?.app_metadata || {}
    const changed =
      root.signedIn !== nextSignedIn ||
      root.currentUserEmail !== nextEmail ||
      root.currentUserId !== nextId
    if (changed) {
      root.update({
        signedIn: nextSignedIn,
        currentUserEmail: nextEmail,
        currentUserId: nextId,
        jwtClaims: nextClaims
      })
    } else if (nextSignedIn) {
      root.update({ jwtClaims: nextClaims })
    }
    if (nextId && nextId !== lastUserId) {
      lastUserId = nextId
      if (typeof onSignIn === 'function') onSignIn({ id: nextId, email: nextEmail })
    } else if (!nextId) {
      lastUserId = null
    }
  })

  // SDK's onAuthStateChange returns the unsubscribe fn directly. The previous
  // sdk-bridge implementation returned `{ data: { subscription: { unsubscribe } } }`
  // and the wrapper here adapted that shape — keep returning a plain function so
  // stopMirrorAuthSession() works unchanged.
  const unsub = () => {
    if (typeof unsubscribe === 'function') unsubscribe()
  }
  root._authMirrorUnsub = unsub
  return unsub
}

export const stopMirrorAuthSession = function stopMirrorAuthSession(root) {
  if (!root?._authMirrorUnsub) return
  root._authMirrorUnsub()
  delete root._authMirrorInstalled
  delete root._authMirrorUnsub
}
